import React from "react";
import Head from "next/head";
import { AxiosError } from "axios";
import styled from "@emotion/styled";
import { GetServerSideProps } from "next";
import { ProfileData } from "@livepix/sdk-js/types/profile";
import SubscriptionSlide from "components/profile/SubscriptionSlide";
import { User, Reward } from "@livepix/sdk-js/types/core";
import { isUsernameValid } from "@livepix/sdk-js/validation";
import useWebservice from "@livepix/components/hooks/useWebservice";
import webserviceClient from "lib/webserviceClient";
import ProfilePage from "components/profile/ProfilePage";
import { Snackbar, Alert } from "@mui/material";

const Title = styled.p`
  margin: 0 0 20px;
  font-size: 20px;
  font-weight: bold;
  text-align: center;
  color: #666;
`;

type Props = {
  amount?: number;
  plan?: string;
  profile: ProfileData;
};

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
  if (!context.params?.username) return { notFound: true };

  const { username } = context.params;
  const { amount, plan } = context.query;

  if (!username || typeof username !== "string" || !isUsernameValid(username)) return { notFound: true };

  try {
    const { data: profile } = await webserviceClient.get<ProfileData>(`/profile/${(username as string).toLowerCase()}`);

    if (!profile.products.subscription.enabled) {
      return { redirect: { permanent: false, destination: `/${username}` } };
    }

    const props: Props = { profile };
    if (amount) props.amount = Number(amount);
    if (plan) props.plan = plan as string;

    return { props };
  } catch (e) {
    if ((e as AxiosError).response?.status !== 404) {
      throw e;
    }

    return { notFound: true };
  }
};

export default function SubscriptionPage({ profile, amount, plan }: Props) {
  const webservice = useWebservice();

  const { data: user } = webservice.loadOnce<User>("/auth/me");
  const { data: rewards } = webservice.loadOnce<Reward[]>(`/profile/${profile.username}/rewards`);

  const [error, setError] = React.useState<string>();
  const [success, setSuccess] = React.useState<string>();

  return (
    <>
      <Head>
        <title>{`Assine ${profile.name} - LivePix`}</title>
        <meta name="description" content={`Envie uma assinatura para ${profile.name} via PIX ou Cartão de Crédito!`} />
        <link rel="canonical" href={`https://livepix.gg/${profile.username.toLowerCase()}/assinatura`} />
      </Head>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(undefined)}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>
      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(undefined)}>
        <Alert severity="success">{success}</Alert>
      </Snackbar>
      <ProfilePage profile={profile}>
        <Title>Assinatura</Title>
        <SubscriptionSlide
          user={user}
          profile={profile}
          rewards={rewards || []}
          suggestedAmount={amount}
          suggestedPlan={plan}
          onError={(message) => setError(message)}
          onResize={() => {}}
        />
      </ProfilePage>
    </>
  );
}
